import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { ExternalLink, Newspaper } from 'lucide-react';

/**
 * NewsPanel — CoinGecko-sourced crypto news feed. Small, focused, no
 * spam. Filters by search text (client-side) and refreshes every 5 min.
 */
export default function NewsPanel({ base }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const [q, setQ] = useState('');
  const [coinOnly, setCoinOnly] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setBusy(true);
      try {
        const { data } = await api.get('/crypto/news', { params: { limit: 30 } });
        if (!cancelled) setItems(data?.items || []);
      } catch { /* noop */ }
      finally { if (!cancelled) setBusy(false); }
    };
    load();
    const iv = setInterval(load, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  const filtered = items.filter((n) => {
    const hay = `${n.title || ''} ${n.description || ''}`.toLowerCase();
    if (q && !hay.includes(q.toLowerCase())) return false;
    if (coinOnly && base && !hay.includes(base.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="border border-[var(--border-default)] bg-[var(--bg-panel)] flex flex-col" data-testid="crypto-news-panel">
      <div className="flex items-center justify-between border-b border-[var(--border-default)] px-2 py-1.5 gap-2">
        <div className="text-[10px] tracking-[0.35em] uppercase text-cyan-sys flex items-center gap-1.5">
          <Newspaper size={11} /> // news feed
        </div>
        {base && (
          <button
            onClick={() => setCoinOnly(v => !v)}
            data-testid="crypto-news-coinonly"
            className={`text-[9px] tracking-widest uppercase px-1.5 py-0.5 border ${coinOnly
              ? 'border-cyan-sys bg-cyan-sys text-black'
              : 'border-[var(--border-default)] text-[var(--txt-secondary)] hover:border-cyan-sys'}`}
          >
            {base} only
          </button>
        )}
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="search…"
          className="bg-black border border-[var(--border-default)] focus:border-cyan-sys outline-none px-1.5 py-0.5 text-[10px] font-mono w-24"
        />
      </div>
      <div className="max-h-72 overflow-y-auto">
        {busy && items.length === 0 && (
          <div className="p-3 text-[10px] text-[var(--txt-muted)] uppercase tracking-widest">// loading feed…</div>
        )}
        {!busy && filtered.length === 0 && (
          <div className="p-3 text-[10px] text-[var(--txt-muted)]">
            {items.length === 0 ? 'News feed is rate-limited — retrying shortly.' : 'No results.'}
          </div>
        )}
        {filtered.map((n) => (
          <a
            key={n.id || n.url}
            href={n.url}
            target="_blank"
            rel="noreferrer"
            className="block px-3 py-2 border-b border-[var(--border-default)] last:border-b-0 hover:bg-[var(--bg-panel-hover)]"
            data-testid="crypto-news-row"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-[11px] text-[var(--txt-primary)] leading-tight line-clamp-2">{n.title}</div>
                <div className="text-[9px] uppercase tracking-widest text-[var(--txt-muted)] mt-0.5">
                  {n.source || '—'}{n.published ? ` · ${new Date(typeof n.published === 'number' ? n.published * 1000 : n.published).toLocaleString()}` : ''}
                </div>
              </div>
              <ExternalLink size={10} className="text-[var(--txt-muted)] mt-1" />
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}
